import config from "../config";
import RouteSegments from "../Routes/RouteSegment";
import { Dashboard_Profile_Data } from "../Helpers/api_helpers";
import { appendRegistrationReturnUrl } from "./registrationReturnUrl";

/** Fields returned by `/api/loginviaotpsend` for `/v4/loginfromotp`. */
export function pickOtpLoginSession(apiPayload) {
  const data = apiPayload?.data ?? apiPayload ?? {};
  return {
    smslogid: data.smslogid ?? data.smslogId ?? data.sms_log_id ?? null,
    jwt: data.jwt ?? data.jwt_token ?? data.token ?? null,
  };
}

export const LOGIN_VIA_OTP_SESSION_KEY = "loginViaOtpSession";

export function storeLoginViaOtpSession(session) {
  if (session?.smslogid != null && session?.jwt) {
    sessionStorage.setItem(LOGIN_VIA_OTP_SESSION_KEY, JSON.stringify(session));
  }
}

export function readLoginViaOtpSession() {
  try {
    return JSON.parse(sessionStorage.getItem(LOGIN_VIA_OTP_SESSION_KEY) || "null");
  } catch {
    return null;
  }
}

export function clearLoginViaOtpSession() {
  sessionStorage.removeItem(LOGIN_VIA_OTP_SESSION_KEY);
}

/** Normalize v4 login `data.status` (Success, SUCCESS, FAILURE, alreadyloggedin, …). */
export function normalizeLoginDataStatus(status) {
  return `${status ?? ""}`.trim().toLowerCase();
}

export function isLoginFailureStatus(loginData) {
  const status = normalizeLoginDataStatus(loginData?.status);
  return (
    status === "failure" ||
    status === "alreadyloggedin" ||
    status === "/invalidate"
  );
}

export function isLoginSuccessStatus(loginData) {
  return normalizeLoginDataStatus(loginData?.status) === "success";
}

export function hasLoginExpiredPopup(loginData) {
  return Boolean(loginData?.popup?.showpopup);
}

/** `/v4/login` returns profilestatus: "delete" when the account was deleted. */
export function isProfileDeletedStatus(loginData) {
  return `${loginData?.profilestatus ?? ""}`.trim().toLowerCase() === "delete";
}

/**
 * getfamilyloginprofilesv3: when every returned member has familynumber "N",
 * treat as deleted-profile case (show ProfileDeletedPopup).
 */
export function isAllFamilyMembersNonFamily(familyMemberDetails) {
  return (
    Array.isArray(familyMemberDetails) &&
    familyMemberDetails.length > 0 &&
    familyMemberDetails.every(
      (member) => `${member?.familynumber ?? ""}`.trim().toUpperCase() === "N"
    )
  );
}

/** Incomplete registration — hand off to v5; v5 calls registerdetailsnew on load. */
export function shouldRedirectToV5Registration(loginData) {
  return Boolean(loginData?.section_key);
}

export const V5_LOGIN_NEXT_PAGE_KEY = "v5LoginNextPage";

export function getV5HandoffNextPage(loginData) {
  return getLoginDataNextPage(loginData);
}

export function shouldHandoffToV5ForNextPage(loginDataOrNextPage) {
  const nextPage =
    typeof loginDataOrNextPage === "string"
      ? loginDataOrNextPage
      : getV5HandoffNextPage(loginDataOrNextPage);
  const normalized = `${nextPage ?? ""}`.trim().toLowerCase();
  return (
    normalized === "expert_support_section" ||
    normalized === "expert_support_completion"
  );
}

function normalizeHandoffToken(value = "") {
  return `${value ?? ""}`.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function shouldResolveChildrenStatusNextPage(loginDataOrNextPage) {
  const nextPage =
    typeof loginDataOrNextPage === "string"
      ? loginDataOrNextPage
      : getLoginDataNextPage(loginDataOrNextPage);
  const normalized = normalizeHandoffToken(nextPage);
  return normalized === "childrenstatus";
}

export function shouldResolveDenominationSelectionNextPage(loginDataOrNextPage) {
  const nextPage =
    typeof loginDataOrNextPage === "string"
      ? loginDataOrNextPage
      : getLoginDataNextPage(loginDataOrNextPage);
  const normalized = normalizeHandoffToken(nextPage);
  return normalized === "denominationselection";
}

export function shouldResolveGothramDetailsNextPage(loginDataOrNextPage) {
  const nextPage =
    typeof loginDataOrNextPage === "string"
      ? loginDataOrNextPage
      : getLoginDataNextPage(loginDataOrNextPage);
  const normalized = normalizeHandoffToken(nextPage);
  return normalized === "gothramdetails";
}

function getMaritalStatusCandidate(profileData) {
  const profile = profileData?.basic_detailsprofile || profileData || {};

  const candidates = [
    profile.basicprofilemartialstatusid,
    profile.basicprofilemaritalstatusid,
    profile.maritalStatusId,
    profile.maritalstatusid,
    profile.marital_status_id,
    profile.basicprofilemartialstatus,
    profile.basicprofilemaritalstatus,
    profile.maritalStatus,
    profile.maritalstatus,
    profile.marital_status,
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate[0];
    }
    return candidate;
  }

  return null;
}

function isNeverMarriedMaritalStatus(profileData) {
  const candidate = getMaritalStatusCandidate(profileData);
  if (candidate == null) return false;

  const id =
    typeof candidate === "object"
      ? candidate.id ?? candidate.maritalStatusId ?? candidate.maritalstatusid
      : candidate;
  if (`${id}`.trim() === "1") {
    return true;
  }

  const name =
    typeof candidate === "object"
      ? candidate.name ?? candidate.label ?? candidate.value ?? ""
      : candidate;
  return normalizeHandoffToken(name) === "nevermarried";
}

function getReligionCandidate(profileData) {
  const profile = profileData?.basic_detailsprofile || profileData || {};

  const candidates = [
    profile.religionId,
    profile.religionid,
    profile.basicprofilereligionid,
    profile.basicprofileReligionId,
    profile.basicprofileReligionID,
    profile.basicprofileReligion,
    profile.religion,
    profile.religion_id,
    profile.profilereligionid,
    profile.profileReligionId,
    profile.profile_religion_id,
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate[0];
    }
    return candidate;
  }

  return null;
}

function isChristianReligion(profileData) {
  const candidate = getReligionCandidate(profileData);
  if (candidate == null) return false;

  const id =
    typeof candidate === "object"
      ? candidate.id ?? candidate.religionId ?? candidate.religionid
      : candidate;
  if (`${id}`.trim() === "18") {
    return true;
  }

  const name =
    typeof candidate === "object"
      ? candidate.name ?? candidate.label ?? candidate.value ?? ""
      : candidate;
  return normalizeHandoffToken(name).includes("christian");
}

function getCasteCandidate(profileData) {
  const profile = profileData?.basic_detailsprofile || profileData || {};

  const candidates = [
    profile.casteId,
    profile.casteid,
    profile.caste,
    profile.caste_id,
    profile.basicprofilecasteid,
    profile.basicprofilecaste,
    profile.profilecasteid,
    profile.profile_caste_id,
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate[0];
    }
    return candidate;
  }

  return null;
}

function hasBrahminCaste(profileData) {
  const candidate = getCasteCandidate(profileData);
  if (candidate == null) return false;

  const id =
    typeof candidate === "object"
      ? candidate.id ?? candidate.casteId ?? candidate.casteid
      : candidate;

  return [
    "48",
    "49",
    "60",
    "64",
    "65",
    "86",
  ].includes(`${id}`.trim());
}

async function fetchDashboardProfileData(loginData) {
  if (!loginData?.userId && !loginData?.userName) {
    return null;
  }

  try {
    const response = await Dashboard_Profile_Data({
      email: loginData?.userName ?? loginData?.email ?? null,
      userId: loginData?.userId ?? null,
      loginFrom:
        localStorage.getItem("LOGIN_DEVICE") ||
        (window.innerWidth < 1024 ? "Mobile Site" : "Desktop"),
    });

    return response?.data?.data ?? null;
  } catch (error) {
    return null;
  }
}

export async function resolveChildrenStatusHandoffSection(loginData) {
  if (!shouldResolveChildrenStatusNextPage(loginData)) {
    return getLoginDataNextPage(loginData);
  }

  if (isNeverMarriedMaritalStatus(loginData)) {
    return "partnermaritalstatus";
  }

  const profileData = await fetchDashboardProfileData(loginData);
  if (isNeverMarriedMaritalStatus(profileData)) {
    return "partnermaritalstatus";
  }

  return "children";
}

export async function resolveDenominationSelectionHandoffSection(loginData) {
  if (!shouldResolveDenominationSelectionNextPage(loginData)) {
    return getLoginDataNextPage(loginData);
  }

  if (isChristianReligion(loginData)) {
    return "denomination";
  }

  const profileData = await fetchDashboardProfileData(loginData);
  if (isChristianReligion(profileData)) {
    return "denomination";
  }

  return "partnerreligion";
}

export async function resolveGothramDetailsHandoffSection(loginData) {
  if (!shouldResolveGothramDetailsNextPage(loginData)) {
    return getLoginDataNextPage(loginData);
  }

  if (hasBrahminCaste(loginData)) {
    return "gothra";
  }

  const profileData = await fetchDashboardProfileData(loginData);
  if (hasBrahminCaste(profileData)) {
    return "gothra";
  }

  return "birthday";
}

export async function resolveLoginV5HandoffData(loginData) {
  if (
    !shouldResolveChildrenStatusNextPage(loginData) &&
    !shouldResolveDenominationSelectionNextPage(loginData) &&
    !shouldResolveGothramDetailsNextPage(loginData)
  ) {
    return loginData;
  }

  const sectionKey = shouldResolveChildrenStatusNextPage(loginData)
    ? await resolveChildrenStatusHandoffSection(loginData)
    : shouldResolveDenominationSelectionNextPage(loginData)
      ? await resolveDenominationSelectionHandoffSection(loginData)
      : await resolveGothramDetailsHandoffSection(loginData);
  if (!sectionKey) {
    return loginData;
  }

  return {
    ...loginData,
    section_key: sectionKey,
  };
}

export function storeV5LoginNextPage(nextPage) {
  if (nextPage) {
    sessionStorage.setItem(V5_LOGIN_NEXT_PAGE_KEY, String(nextPage));
  } else {
    sessionStorage.removeItem(V5_LOGIN_NEXT_PAGE_KEY);
  }
}

export function readV5LoginNextPage() {
  return sessionStorage.getItem(V5_LOGIN_NEXT_PAGE_KEY) || "";
}

export function clearV5LoginNextPage() {
  sessionStorage.removeItem(V5_LOGIN_NEXT_PAGE_KEY);
}

export function getSelectedVersion(loginData = {}) {
  return loginData?.selectedversion ?? loginData?.selectedVersion ?? "";
}

export function usesLiteExperience(selectedVersion) {
  const version = `${selectedVersion ?? ""}`.toUpperCase();
  return version === "V3" || version === "V4";
}

/**
 * Home route after login when API returns nextPage "/" or empty.
 * V3/V4 users land on matches; others use legacy home.
 */
export function getLoginDataNextPage(loginData) {
  return loginData?.nextPage ?? loginData?.nextpage ?? "";
}

/**
 * Maps login API nextPage to a real React route.
 * API often returns "dashboard" / "home" which are NOT registered —
 * navigating there hits App's `*` catch-all and bounces to `/`.
 */
export function resolvePostLoginPath(loginData, selectedVersion) {
  const nextPage = `${getLoginDataNextPage(loginData)}`.trim();
  const normalized = nextPage.toLowerCase().replace(/^\/+/, "");
  const isLite = usesLiteExperience(
    selectedVersion ?? getSelectedVersion(loginData)
  );

  if (!nextPage || nextPage === "/" || normalized === "signin") {
    return isLite
      ? RouteSegments.MATCH_LITE
      : RouteSegments.ONLINE_MATRIMONY_WEBSITE;
  }

  // Always map app-home aliases — never navigate to /dashboard (no such route)
  if (
    normalized === "dashboard" ||
    normalized === "home" ||
    normalized === "matchespage" ||
    normalized === "matches" ||
    normalized === "matchs" ||
    normalized === "match" ||
    normalized === "matchlist" ||
    normalized === "match_list" ||
    normalized === "new/matches" ||
    normalized === "new/matchs"
  ) {
    return RouteSegments.MATCH_LITE;
  }

  if (nextPage.startsWith("/")) {
    return nextPage;
  }

  return `/${nextPage}`;
}

/**
 * Build v5 registration URL with auth context.
 * v5 project should call registerdetailsnew once and route to the matching step.
 */
export function buildV5RegistrationUrl(loginData, token, extraParams = {}) {
  const base =
    config.v5RegistrationUrl || "https://v5-uat.kalyanmatrimony.com/registration";
  const url = base.includes("://")
    ? new URL(base)
    : new URL(base, window.location.origin);

  if (loginData?.section_key) {
    url.searchParams.set("section_key", loginData.section_key);
  }
  if (loginData?.userId != null) {
    url.searchParams.set("userId", String(loginData.userId));
  }
  if (loginData?.userBasicDetailsId != null) {
    url.searchParams.set("userBasicDetailsId", String(loginData.userBasicDetailsId));
  }
  if (loginData?.profileFor) {
    url.searchParams.set("profileFor", loginData.profileFor);
  }
  if (loginData?.userName) {
    url.searchParams.set("userName", loginData.userName);
  }
  if (loginData?.profileUuid) {
    url.searchParams.set("profileUuid", String(loginData.profileUuid));
  }
  if (loginData?.motherToungeId != null) {
    url.searchParams.set("motherToungeId", String(loginData.motherToungeId));
  }
  if (loginData?.selectedversion) {
    url.searchParams.set("selectedversion", loginData.selectedversion);
  }
  if (loginData?.mobileNumber != null) {
    url.searchParams.set("mobileNumber", String(loginData.mobileNumber));
  }
  if (token) {
    url.searchParams.set("token", token);
  }

  Object.entries(extraParams || {}).forEach(([key, value]) => {
    if (value != null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  });

  appendRegistrationReturnUrl(url);

  return url.toString();
}

export const PENDING_V5_REGISTRATION_URL_KEY = "pendingV5RegistrationUrl";
export const V5_REGISTRATION_HANDOFF_URL_KEY = "v5RegistrationHandoffUrl";

export function storeV5RegistrationHandoffUrl(loginData, token, extraParams = {}) {
  const url = buildV5RegistrationUrl(loginData, token, extraParams);
  sessionStorage.setItem(V5_REGISTRATION_HANDOFF_URL_KEY, url);
  return url;
}

export function readV5RegistrationHandoffUrl() {
  return sessionStorage.getItem(V5_REGISTRATION_HANDOFF_URL_KEY) || "";
}

export function clearV5RegistrationHandoffUrl() {
  sessionStorage.removeItem(V5_REGISTRATION_HANDOFF_URL_KEY);
}

/**
 * Open V5 registration in a new tab only (never same-tab redirect).
 * Always stores handoff URL; auto window.open often fails without a user click.
 */
/**
 * @param {{ sameTab?: boolean }} options — use sameTab for post-login incomplete registration
 */
export function redirectToV5Registration(loginData, token, options = {}) {
  const {
    sameTab = false,
    extraParams = {},
  } = options;
  const url = storeV5RegistrationHandoffUrl(loginData, token, extraParams);

  if (sameTab) {
    sessionStorage.removeItem(PENDING_V5_REGISTRATION_URL_KEY);
    window.location.href = url;
    return { opened: true, url, sameTab: true };
  }

  const openedTab = window.open(url, "_blank", "noopener,noreferrer");

  if (!openedTab) {
    sessionStorage.setItem(PENDING_V5_REGISTRATION_URL_KEY, url);
    return { opened: false, url };
  }

  sessionStorage.removeItem(PENDING_V5_REGISTRATION_URL_KEY);
  return { opened: true, url };
}

/** Build `/v4/login` request body (shared by saga and landing-page login). */
export function buildLoginV4RequestBody(user, deviceFlag = "no") {
  const deviceYes =
    `${deviceFlag}`.toLowerCase() === "yes" || `${deviceFlag}`.toLowerCase() === "y";
  return {
    email: user.name ?? user.email ?? null,
    password: user.password,
    loginFrom:
      localStorage.getItem("LOGIN_DEVICE") ||
      (window.innerWidth < 1024 ? "Mobile Site" : "Desktop"),
    ioskey: "",
    androidkey: "",
    device: deviceYes ? "Yes" : "No",
    familynumber: user.familynumber ?? "N",
    bundleid: user.bundleid ?? "",
    ...(user.webKey != null && { webKey: user.webKey }),
    ...(user.offlinepack != null && { offlinepack: user.offlinepack }),
    ...(user.otp != null && { otp: user.otp }),
  };
}

/** True when login completed and user should enter the main app (not V5 registration). */
export function isLoginCompleteSuccess(loginData) {
  return isLoginSuccessStatus(loginData);
}

export function shouldHandleAsLoginError(loginData) {
  if (hasLoginExpiredPopup(loginData)) {
    return false;
  }
  if (isProfileDeletedStatus(loginData)) {
    return false;
  }
  if (shouldRedirectToV5Registration(loginData)) {
    return false;
  }
  return isLoginFailureStatus(loginData) && !isLoginCompleteSuccess(loginData);
}

/** Sidebar / profile menu — new tab only. */
export function openV5Registration(loginData, token) {
  return redirectToV5Registration(loginData, token);
}

/** Plain V5 registration URL — new tab only. */
export function openV5RegistrationUrl(url) {
  if (!url) {
    return { opened: false, url: "" };
  }
  const openedTab = window.open(url, "_blank", "noopener,noreferrer");
  if (!openedTab) {
    sessionStorage.setItem(PENDING_V5_REGISTRATION_URL_KEY, url);
    return { opened: false, url };
  }
  sessionStorage.removeItem(PENDING_V5_REGISTRATION_URL_KEY);
  return { opened: true, url };
}

export function readPendingV5RegistrationUrl() {
  return sessionStorage.getItem(PENDING_V5_REGISTRATION_URL_KEY) || "";
}

export function clearPendingV5RegistrationUrl() {
  sessionStorage.removeItem(PENDING_V5_REGISTRATION_URL_KEY);
}

export function persistLoginSessionForHandoff(loginData, token) {
  if (token) {
    localStorage.setItem("accessToken", token);
  }
  if (loginData?.userId != null) {
    localStorage.setItem("LOGIN_ID", String(loginData.userId));
  }
  if (loginData?.userName) {
    localStorage.setItem("LOGIN_NAME", loginData.userName);
    localStorage.setItem("LOGIN_EMAIL", loginData.userName);
  }
  if (loginData?.selectedversion) {
    if (usesLiteExperience(loginData.selectedversion)) {
      localStorage.setItem("isV3User", "true");
    }
  }
}
